import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getOnboardingDetails, completeOnboarding } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  CheckCircle,
  Shield,
  Building2,
  User,
  Lock,
  Loader2,
  AlertTriangle,
  Eye,
  EyeOff,
  ArrowRight
} from 'lucide-react';

export default function CompleteOnboarding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [details, setDetails] = useState(null);
  const [error, setError] = useState(null);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadDetails = async () => {
      if (!token) {
        setError('No onboarding token provided');
        setLoading(false);
        return;
      }
      
      try {
        const response = await getOnboardingDetails(token);
        setDetails(response.data);
      } catch (err) {
        console.error('Error loading onboarding details:', err);
        setError(err.response?.data?.detail || 'Failed to load onboarding details');
      } finally {
        setLoading(false);
      }
    };
    
    loadDetails();
  }, [token]);

  const getRiskColor = (level) => {
    switch (level) {
      case 'low': return 'text-green-600 bg-green-50';
      case 'medium': return 'text-yellow-600 bg-yellow-50';
      case 'high': return 'text-orange-600 bg-orange-50';
      case 'critical': return 'text-red-600 bg-red-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const validatePassword = () => {
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) return;
    
    setSubmitting(true);
    try {
      const response = await completeOnboarding(token, password);
      
      // Log the user in
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success('Account created successfully!');
      
      // Navigate to dashboard
      navigate('/');
    } catch (err) {
      console.error('Error completing onboarding:', err);
      toast.error(err.response?.data?.detail || 'Failed to complete onboarding');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-12 px-4">
        <div className="max-w-md mx-auto">
          <div className="text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_safetyguard-4/artifacts/97rz3phl_players-health-lockup-color.png"
              alt="Players Health"
              className="h-10 mx-auto"
            />
          </div>
          
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <h2 className="text-xl font-semibold mb-2">Unable to Complete Onboarding</h2>
                <p className="text-slate-600 mb-6">{error}</p>
                <Button onClick={() => navigate('/assess')} variant="outline">
                  Start New Assessment
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_safetyguard-4/artifacts/97rz3phl_players-health-lockup-color.png"
            alt="Players Health"
            className="h-10 mx-auto"
          />
        </div>

        <Card className="shadow-xl">
          <CardHeader className="text-center border-b pb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <CardTitle className="text-2xl">Complete Your Account</CardTitle>
            <CardDescription>
              Set up your password to access your Risk OS dashboard
            </CardDescription>
          </CardHeader>
          
          <CardContent className="pt-6 space-y-6">
            {/* Organization & Assessment Summary */}
            {details && (
              <>
                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-3">
                    <Building2 className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-semibold">{details.organization?.name}</p>
                      <p className="text-sm text-slate-500 capitalize">{details.organization?.org_type} Organization</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <User className="w-5 h-5 text-slate-400" />
                    <div>
                      <p className="font-medium">{details.name}</p>
                      <p className="text-sm text-slate-500">{details.email}</p>
                    </div>
                  </div>
                </div>

                {/* Assessment Score */}
                {details.assessment && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Your Risk Score</p>
                        <p className="text-2xl font-bold">{details.assessment.score}</p>
                      </div>
                      <Badge className={getRiskColor(details.assessment.risk_level)}>
                        {details.assessment.risk_level?.toUpperCase()} RISK
                      </Badge>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Password Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password">Create Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter a secure password"
                    className="pl-10 pr-10"
                    required
                    minLength={8}
                    data-testid="input-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Minimum 8 characters</p>
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm your password"
                    className="pl-10"
                    required
                    data-testid="input-confirm-password"
                  />
                </div>
              </div>

              <Alert>
                <CheckCircle className="w-4 h-4" />
                <AlertDescription>
                  You'll be assigned as the <strong>Organization Administrator</strong> with full access to manage 
                  your organization's risk profile, incidents, and training.
                </AlertDescription>
              </Alert>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={submitting}
                data-testid="btn-complete"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    Complete Setup
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>Already have an account? <a href="/login" className="text-blue-600 hover:underline">Log in</a></p>
          <p className="mt-2">© 2026 Players Health. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
